import { fetchAuthSession } from '@aws-amplify/auth';

import appConfig from '../config';

function makeCdnApiError(message, statusCode) {
  return Object.assign(new Error(message), { name: 'CdnApiError', statusCode });
}

class CdnApiClient {
  constructor() {
    this._accessToken = null;
    this._authFailureCallback = null;
  }

  static getClient() {
    if (!CdnApiClient._instance) {
      CdnApiClient._instance = new CdnApiClient();
    }
    return CdnApiClient._instance;
  }

  setAuthFailureCallback(callback) {
    this._authFailureCallback = callback;
  }

  async _resolveToken() {
    if (this._accessToken) return this._accessToken;
    try {
      const session = await fetchAuthSession();
      this._accessToken = session.tokens?.accessToken?.toString() ?? null;
    } catch {
      // unauthenticated — proceed without a token
    }
    return this._accessToken;
  }

  async request(endpoint, options = {}) {
    const baseUrl = options.baseUrl !== undefined ? options.baseUrl : appConfig.cdnApiUrl;
    let url = `${baseUrl}${endpoint}`;

    if (options.queryParams) {
      const qs = new URLSearchParams(
        Object.entries(options.queryParams).map(([k, v]) => [k, String(v)]),
      ).toString();
      url = `${url}?${qs}`;
    }

    const accessToken = await this._resolveToken();

    const isFormData = options.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const body = options.body != null
      ? isFormData ? options.body : JSON.stringify(options.body)
      : undefined;

    const method = options.method ?? 'GET';
    const response = await fetch(url, { method, headers, body });

    if (response.status === 401) {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const newToken = session.tokens?.accessToken?.toString();
        if (newToken) {
          this._accessToken = newToken;
          headers.Authorization = `Bearer ${newToken}`;
          const retry = await fetch(url, { method, headers, body });
          if (retry.ok) {
            const text = await retry.text();
            return text ? JSON.parse(text) : undefined;
          }
        }
      } catch {
        // refresh failed
      }
      this._accessToken = null;
      if (this._authFailureCallback) this._authFailureCallback();
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw makeCdnApiError(
        `CDN API request ${url} failed: ${response.status} ${errorText}`,
        response.status,
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : undefined;
  }
}

export const cdnApiClient = CdnApiClient.getClient();
